import { VideoSource } from '@cms/ts/types/enums/VideoSource';
import { diffDOM } from '@cms/ts/utilities/cms.diff.dom';
import { parseVideoUrl } from '@cms/ts/utilities/cms.parsed.video';
import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';

export default class CMSVideoEmbed extends TKCustomElementFactory {
    url?: string;
    titleText?: string;
    description?: string;
    thumbnailURL?: string;
    autoplay?: boolean;
    loop?: boolean;
    muted?: boolean;
    loaded: boolean = false;

    constructor() {
        super();
    }

    connectedCallback(): void {
        this.render();
    }

    static get observedAttributes(): string[] {
        return [
            'data-url',
            'data-title',
            'data-description',
            'data-thumbnail-url',
            'data-autoplay',
            'data-loop',
            'data-muted',
        ];
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
        if (name === 'data-url' && oldValue !== newValue) this.url = newValue;
        if (name === 'data-title' && oldValue !== newValue) this.titleText = newValue;
        if (name === 'data-description' && oldValue !== newValue) this.description = newValue;
        if (name === 'data-thumbnail-url' && oldValue !== newValue) this.thumbnailURL = newValue;
        if (name === 'data-autoplay' && oldValue !== newValue) this.autoplay = newValue === '';
        if (name === 'data-loop' && oldValue !== newValue) this.loop = newValue === '';
        if (name === 'data-muted' && oldValue !== newValue) this.muted = newValue === '';
        if (oldValue !== newValue) {
            this.render();
        }
    }

    handleClick() {
        if (this.loaded || !this.url) return;
        const url = new URL(this.url);
        this.autoplay && url.searchParams.set('autoplay', '1');
        this.loop && url.searchParams.set('loop', '1');
        this.muted && url.searchParams.set('mute', '1');
        const iframeHTML = `<iframe
            src="${this.url}"
            title="${this.titleText || 'Video'}"
            frameborder="0"
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
            width="100%"
            height="100%"
        ></iframe>`;
        this.innerHTML = iframeHTML;
        this.loaded = true;
        diffDOM(this, iframeHTML);
    }

    render(): void {
        if (this.loaded || !this.url) return;

        const { source, id } = parseVideoUrl(this.url);
        if (!source || !id) return;

        let thumb = this.thumbnailURL;

        // Auto-Thumbnail für YouTube
        if (!thumb && source === VideoSource.YOUTUBE && id) {
            thumb = `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
        }

        const html = `
            <div class="tk-video-embed__thumbnail" role="button" aria-label="Play video" data-thumbnail>
                <img src="${thumb ?? ''}" alt="${this.titleText ?? 'Video thumbnail'}">
                <div class="tk-video-embed__overlay">
                <div class="tk-video-embed__play-button">▶</div>
                ${this.titleText ? `<h3 class="tk-video-embed__title">${this.titleText}</h3>` : ''}
                ${this.description ? `<p class="tk-video-embed__description">${this.description}</p>` : ''}
                </div>
            </div>`;

        diffDOM(this, html, { wrapMultipleRoots: true });

        const thumbnailElement = this.querySelector('[data-thumbnail]');
        if (!thumbnailElement) return;
        const action = this.handleClick.bind(this);
        this.pushListener({
            event: 'click',
            element: thumbnailElement,
            action,
        });
    }
}
